import React, { useState } from 'react';
import {
  Box, Typography, Paper, Button, Chip, Table, TableHead, TableBody,
  TableRow, TableCell, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, CircularProgress, IconButton, Collapse,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import WmsNotification, { useWmsNotification } from '../../shared/WmsNotification';
import useWmsPolling from '../../shared/useWmsPolling';
import receivingService, { ReceivingSessionDto, ReceivingStats } from '../../services/receivingService';
import WmsStationHeader from '../../shared/WmsStationHeader';
import WmsScannerPanel from '../../shared/WmsScannerPanel';
import WmsListSection from '../../shared/WmsListSection';
import { useTranslation } from 'react-i18next';

interface ReceivingReadyProps {
  cashierName: string;
  warehouseId: string;
  onSessionStart: (session: ReceivingSessionDto) => void;
  onSessionResume: (session: ReceivingSessionDto) => void;
  onQuickStart: (session: ReceivingSessionDto, barcode: string) => void;
}

const ReceivingReady: React.FC<ReceivingReadyProps> = ({
  cashierName, warehouseId, onSessionStart, onSessionResume, onQuickStart,
}) => {
  const { t } = useTranslation('warehouse');
  const { notification, notify, clear } = useWmsNotification();
  const [showNew, setShowNew] = useState(false);
  const [supplierName, setSupplierName] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [creating, setCreating] = useState(false);
  // Bugünkü teslimatlar popup'ı (mavi rozete tıklanınca).
  const [todayOpen, setTodayOpen] = useState(false);
  const [todayLoading, setTodayLoading] = useState(false);
  const [todayList, setTodayList] = useState<ReceivingSessionDto[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedDetail, setExpandedDetail] = useState<ReceivingSessionDto | null>(null);
  const [itemsLoading, setItemsLoading] = useState(false);

  const { data: sessions, refresh } = useWmsPolling<ReceivingSessionDto[]>(
    async () => {
      const resp = await receivingService.getSessions(warehouseId, { status: 'InProgress' });
      return resp.data ?? [];
    },
    5000,
    !!warehouseId
  );

  const { data: stats } = useWmsPolling<ReceivingStats>(
    () => receivingService.getStats(warehouseId),
    10000,
    !!warehouseId
  );

  const handleCreateSession = async () => {
    setCreating(true);
    try {
      const session = await receivingService.createSession({
        WarehouseId: warehouseId,
        SupplierName: supplierName || undefined,
        DocumentNumber: documentNumber || undefined,
        Notes: notes || undefined,
        CashierName: cashierName || undefined,
      });
      setShowNew(false);
      setSupplierName('');
      setDocumentNumber('');
      setNotes('');
      onSessionStart(session);
    } catch {
      notify({ type: 'error', message: t('screens.receiving.ready.errorCreateFailed') });
    }
    setCreating(false);
  };

  const handleOpenToday = async () => {
    setTodayOpen(true);
    setExpandedId(null);
    setExpandedDetail(null);
    setTodayLoading(true);
    try {
      const resp = await receivingService.getSessions(warehouseId, { pageSize: 200 });
      const all: ReceivingSessionDto[] = resp.data ?? [];
      const todayStr = new Date().toDateString();
      setTodayList(all.filter(s => new Date(s.startedAt).toDateString() === todayStr));
    } catch {
      notify({ type: 'error', message: t('screens.receiving.ready.errorTodayLoadFailed') });
    } finally {
      setTodayLoading(false);
    }
  };

  const handleToggleExpand = async (sessionId: string) => {
    if (expandedId === sessionId) {
      setExpandedId(null);
      setExpandedDetail(null);
      return;
    }
    setExpandedId(sessionId);
    setExpandedDetail(null);
    setItemsLoading(true);
    try {
      const detail = await receivingService.getSessionDetail(warehouseId, sessionId);
      setExpandedDetail(detail);
    } catch {
      notify({ type: 'error', message: t('screens.receiving.ready.errorItemsLoadFailed') });
    } finally {
      setItemsLoading(false);
    }
  };

  const statusLabel = (status: string) =>
    status === 'Completed' ? t('screens.receiving.ready.statusCompleted')
    : status === 'Cancelled' ? t('screens.receiving.ready.statusCancelled')
    : t('screens.receiving.ready.statusInProgress');

  const statusColor = (status: string): 'success' | 'error' | 'warning' =>
    status === 'Completed' ? 'success'
    : status === 'Cancelled' ? 'error'
    : 'warning';

  const handleQuickScan = async (barcode: string) => {
    try {
      const session = await receivingService.createSession({ WarehouseId: warehouseId, CashierName: cashierName || undefined });
      onQuickStart(session, barcode);
    } catch {
      notify({ type: 'error', message: t('screens.receiving.ready.errorQuickStartFailed') });
    }
  };

  const inProgressList = sessions || [];

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
      <WmsNotification notification={notification} onClose={clear} />

      <WmsStationHeader
        title={t('screens.receiving.ready.stationTitle')}
        subtitle={t('screens.receiving.ready.operator', { name: cashierName })}
        badgeLabel={t('screens.receiving.ready.todayBadge', { sessions: stats?.todaySessions ?? 0, items: stats?.todayItems ?? 0 })}
        badgeColor="info"
        badgeIcon={<LocalShippingIcon />}
        onBadgeClick={handleOpenToday}
      />

      <Paper sx={{ p: { xs: 1.5, sm: 3 }, bgcolor: 'background.paper' }}>
        <Typography variant="h6" sx={{ mb: 2, textAlign: 'center', fontWeight: 700, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          {t('screens.receiving.ready.newDeliveryHeading')}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="large"
            onClick={() => setShowNew(true)}
            sx={{
              minWidth: { xs: '100%', sm: 220 },
              maxWidth: { xs: '100%', sm: 320 },
              width: { xs: '100%', sm: 'auto' },
              fontSize: { xs: 14, sm: 16 },
              fontWeight: 700,
            }}
          >
            {t('screens.receiving.ready.newDeliveryButton')}
          </Button>
        </Box>
        <WmsScannerPanel
          title={t('screens.receiving.ready.quickScanTitle')}
          placeholder={t('screens.receiving.ready.quickScanPlaceholder')}
          onScan={handleQuickScan}
          maxWidth={500}
        />
      </Paper>

      <WmsListSection
        title={t('screens.receiving.ready.inProgressTitle')}
        count={inProgressList.length}
        onRefresh={refresh}
        hasData={inProgressList.length > 0}
        emptyText={t('screens.receiving.ready.inProgressEmpty')}
      >
        <Paper sx={{ bgcolor: 'background.paper', overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('screens.receiving.ready.colDeliveryNo')}</TableCell>
                <TableCell>{t('screens.receiving.ready.colStart')}</TableCell>
                <TableCell>{t('screens.receiving.ready.colSupplier')}</TableCell>
                <TableCell align="center">{t('common.product')}</TableCell>
                <TableCell align="center">{t('common.status')}</TableCell>
                <TableCell align="center">{t('common.action')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inProgressList.map(s => (
                <TableRow key={s.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{s.sessionNumber}</TableCell>
                  <TableCell>{new Date(s.startedAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</TableCell>
                  <TableCell>{s.supplierName || '-'}</TableCell>
                  <TableCell align="center">{t('screens.receiving.ready.itemCount', { count: s.totalItems })}</TableCell>
                  <TableCell align="center">
                    <Chip label={t('screens.receiving.ready.statusInProgress')} size="small" color="warning" />
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<PlayArrowIcon />}
                      onClick={() => onSessionResume(s)}
                    >
                      {t('screens.receiving.ready.resume')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </WmsListSection>

      <Dialog open={todayOpen} onClose={() => setTodayOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{t('screens.receiving.ready.todayTitle')}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip label={t('screens.receiving.ready.todayDeliveryCount', { count: todayList.length })} color="info" variant="outlined" />
            <Chip label={t('screens.receiving.ready.todayTotalItems', { count: todayList.reduce((sum, s) => sum + (s.totalItems || 0), 0) })} color="primary" variant="outlined" />
          </Box>

          {todayLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : todayList.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              {t('screens.receiving.ready.todayEmpty')}
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={40} />
                  <TableCell>{t('screens.receiving.ready.colDeliveryNo')}</TableCell>
                  <TableCell>{t('screens.receiving.ready.colTime')}</TableCell>
                  <TableCell align="center">{t('screens.receiving.ready.colProductVariety')}</TableCell>
                  <TableCell align="center">{t('screens.receiving.ready.colTotalQty')}</TableCell>
                  <TableCell align="center">{t('common.status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {todayList.map(s => (
                  <React.Fragment key={s.id}>
                    <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => handleToggleExpand(s.id)}>
                      <TableCell>
                        <IconButton size="small">
                          {expandedId === s.id ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{s.sessionNumber}</TableCell>
                      <TableCell>{new Date(s.startedAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</TableCell>
                      <TableCell align="center">{s.totalProducts}</TableCell>
                      <TableCell align="center">{s.totalItems}</TableCell>
                      <TableCell align="center">
                        <Chip label={statusLabel(s.status)} size="small" color={statusColor(s.status)} />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={6} sx={{ py: 0, border: 0 }}>
                        <Collapse in={expandedId === s.id} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 1.5, px: 2 }}>
                            {itemsLoading && expandedId === s.id ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
                            ) : (expandedDetail?.items?.length ?? 0) === 0 ? (
                              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                                {t('screens.receiving.ready.noItems')}
                              </Typography>
                            ) : (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>{t('common.product')}</TableCell>
                                    <TableCell>{t('common.barcode')}</TableCell>
                                    <TableCell align="center">{t('common.quantity')}</TableCell>
                                    <TableCell>{t('common.location')}</TableCell>
                                    <TableCell align="center">{t('common.status')}</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {expandedDetail?.items?.map(it => (
                                    <TableRow key={it.id}>
                                      <TableCell>{it.productName || '-'}</TableCell>
                                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{it.barcode || '-'}</TableCell>
                                      <TableCell align="center">{it.receivedQuantity}</TableCell>
                                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{it.locationCode || '-'}</TableCell>
                                      <TableCell align="center">
                                        <Chip
                                          label={it.status === 'PutAway' ? t('screens.receiving.ready.itemStatusPutAway') : it.status === 'Cancelled' ? t('screens.receiving.ready.statusCancelled') : t('screens.receiving.ready.itemStatusReceived')}
                                          size="small"
                                          color={it.status === 'PutAway' ? 'success' : it.status === 'Cancelled' ? 'error' : 'default'}
                                        />
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTodayOpen(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showNew} onClose={() => setShowNew(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('screens.receiving.ready.newDeliveryDialogTitle')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField
            label={t('screens.receiving.ready.supplierNameLabel')}
            value={supplierName}
            onChange={e => setSupplierName(e.target.value)}
            fullWidth size="small"
          />
          <TextField
            label={t('screens.receiving.ready.documentNumberLabel')}
            value={documentNumber}
            onChange={e => setDocumentNumber(e.target.value)}
            fullWidth size="small"
          />
          <TextField
            label={t('screens.receiving.ready.notesLabel')}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            fullWidth size="small" multiline rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNew(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleCreateSession} disabled={creating}>
            {t('screens.receiving.ready.startDelivery')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReceivingReady;
